import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { downloadFileAs } from '../../utils/downloadFile';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db, storage } from '../../config/firebaseConfig';
import { toast } from 'react-toastify';
import styles from './FileUploadModal.module.css';
import { FaUpload, FaTimes, FaFile, FaFilePdf, FaFileWord, FaFileExcel, FaFileImage, FaFolder, FaTrash, FaEye, FaDownload } from 'react-icons/fa';

const FileUploadModal = ({ employee, onClose }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [fileCategory, setFileCategory] = useState('');
  const [cvDocuments, setCVDocuments] = useState([]);
  const [cvLoading, setCVLoading] = useState(false);
  const inputRef = useRef(null);
  const modalRef = useRef(null);

  // List of available file categories
  const fileCategories = [
    'Nüfuz Cüzdanı Fotokopisi',
    'İkametgah',
    'Adli Sicil Kaydı',
    'Sağlık Raporu',
    'Nüfuz Kayıt Örneği',
    'Aile Durumu Bildirimi',
    'Lise Diploma',
    'Lisans Diploma',
    'Yüksek Lisans Diploma',
    'Doktora Diploma',
    'SGK Giriş Bildirgesi',
    'İşe Giriş Sözleşme',
    'İşten Ayrılış Sözleşme',
    'Kan Kartı',
    'Resim',
    'Sertifika',
    'Ön Lisans Diploma',
    'Diğer'
  ];

  // Close modal when clicking outside
  const handleOverlayClick = useCallback((e) => {
    if (e.target === modalRef.current && !uploading) {
      onClose();
    }
  }, [onClose, uploading]);

  // Fetch CV/GCP/IATA documents
  const fetchCVDocuments = useCallback(async () => {
    if (!employee?.id) return;
    
    setCVLoading(true);
    try {
      const q = query(
        collection(db, 'CVDocuments'),
        where('employeeId', '==', employee.id)
      );
      
      const snapshot = await getDocs(q);
      const docs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Manuel sıralama - client-side
      docs.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.uploadedAt || 0);
        const dateB = b.createdAt?.toDate?.() || new Date(b.uploadedAt || 0);
        return dateB - dateA; // Yeni dosyalar önce
      });
      
      setCVDocuments(docs);
    } catch (error) {
      console.error('CV belgeleri yüklenemedi:', error);
    } finally {
      setCVLoading(false);
    }
  }, [employee?.id]);

  // Add escape key listener to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !uploading) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, uploading]);

  // Load CV documents when modal opens
  useEffect(() => {
    fetchCVDocuments();
  }, [fetchCVDocuments]);

  // Get file icon based on file type
  const getFileIcon = (fileType) => {
    const type = fileType.toLowerCase();
    if (type.includes('pdf')) return <FaFilePdf />;
    if (type.includes('word') || type.includes('doc')) return <FaFileWord />;
    if (type.includes('excel') || type.includes('sheet') || type.includes('xls')) return <FaFileExcel />;
    if (type.includes('image') || type.includes('png') || type.includes('jpg') || type.includes('jpeg')) return <FaFileImage />;
    return <FaFile />;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    await handleFiles(files);
  };

  const handleChange = async (e) => {
    const files = e.target.files;
    await handleFiles(files);
  };

  const createSafeStoragePath = (documentId, fileName) => {
    // Ensure we have valid inputs
    if (!documentId || !fileName) {
      throw new Error('Invalid document ID or file name');
    }
  
    // Convert filename to string and get extension
    const fileNameString = String(fileName);
    const fileExtension = fileNameString.split('.').pop().toLowerCase();
    const timestamp = new Date().getTime();
    
    // Create a safe path using just the document ID and timestamp
    return `employees/${documentId}/${timestamp}.${fileExtension}`;
  };

  const handleFiles = async (files) => {
    if (!fileCategory) {
      toast.error('Lütfen dosya türünü seçiniz!');
      return;
    }

    // Check if a file with the same category already exists
    const existingFile = employee?.files?.find(file => file.category === fileCategory);
    if (existingFile) {
      toast.error(`Bu dosya türü (${fileCategory}) zaten yüklenmiş! Önce mevcut dosyayı silin.`);
      return;
    }

    setUploading(true);
    try {
      const documentId = employee?.id;
      
      if (!documentId) {
        throw new Error('Invalid employee document ID');
      }
  
      // Convert FileList to Array
      const filesArray = Array.from(files);
      const uploadedFiles = [];

      for (const file of filesArray) {
        const storagePath = createSafeStoragePath(documentId, file.name);
        const storageRef = ref(storage, storagePath);
        // Storage'daki nesne adı zaman damgasından üretiliyor; dosyanın özgün
        // adı contentDisposition ile saklanır ki adres doğrudan açıldığında da
        // doğru adla inisin. (UTF-8 filename* Türkçe karakterler için gerekli.)
        // "attachment" değil "inline": böylece PDF/resim önizlemesi tarayıcıda
        // açılmaya devam eder, indirilen dosya yine özgün adı alır.
        await uploadBytes(storageRef, file, {
          contentType: file.type || undefined,
          contentDisposition: `inline; filename*=UTF-8''${encodeURIComponent(file.name)}`,
        });
        const downloadURL = await getDownloadURL(storageRef);
        const employeeRef = doc(db, 'Employees', documentId);

        const newFile = {
          name: file.name,
          path: storagePath,
          type: file.type || file.name.split('.').pop().toLowerCase(),
          url: downloadURL,
          uploadDate: new Date().toISOString(),
          category: fileCategory
        };
        
        await updateDoc(employeeRef, {
          files: arrayUnion(newFile)
        });

        uploadedFiles.push(newFile);
      }
      
      toast.success('Dosyalar başarıyla yüklendi!');
      
      // Update the local employee object with the new files
      const updatedEmployee = {
        ...employee,
        files: [...(employee.files || []), ...uploadedFiles]
      };
      
      // Update the parent component with new data but keep modal open
      onClose(updatedEmployee, true);
      
      // Clear the file input
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Dosya yükleme hatası!');
    }
    setUploading(false);
  };

  const handleDeleteFile = async (file) => {
    if (!window.confirm('Bu dosyayı silmek istediğinizden emin misiniz?')) {
      return;
    }

    setDeleting(true);
    try {
      // Delete from Firebase Storage
      const storageRef = ref(storage, file.path);
      await deleteObject(storageRef);

      // Remove from Firestore
      const employeeRef = doc(db, 'Employees', employee.id);
      await updateDoc(employeeRef, {
        files: arrayRemove(file)
      });

      // Update local state
      const updatedEmployee = {
        ...employee,
        files: employee.files.filter(f => f.path !== file.path)
      };

      // Update parent component
      onClose(updatedEmployee, true);
      toast.success('Dosya başarıyla silindi!');
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Dosya silinirken bir hata oluştu!');
    }
    setDeleting(false);
  };

  return (
    <div 
      className={styles.modalOverlay} 
      ref={modalRef}
      onClick={handleOverlayClick}
    >
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Dosya Yükleme</h2>
          <button 
            onClick={onClose} 
            className={styles.closeButton}
            disabled={uploading}
            aria-label="Kapat"
          >
            <FaTimes />
          </button>
        </div>
        
        <div className={styles.modalBody}>
          <div className={styles.formGroup}>
            <label 
              htmlFor="fileCategory" 
              className={styles.formLabel}
            >
              Dosya Türü:
            </label>
            <select
              id="fileCategory"
              value={fileCategory}
              onChange={(e) => setFileCategory(e.target.value)}
              required
              className={styles.formSelect}
              disabled={uploading}
            >
              <option value="">Seçiniz</option>
              {fileCategories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <input
            ref={inputRef}
            type="file"
            multiple
            onChange={handleChange}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            className={styles.fileInput}
            disabled={uploading}
          />
          
          <div 
            className={`${styles.dropzone} ${dragActive ? styles.dragActive : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => inputRef.current.click()}
          >
            <div className={styles.dropzoneIcon}>
              <FaUpload />
            </div>
            <p className={styles.dropzoneText}>Dosyalarınızı Sürükleyin</p>
            <p className={styles.dropzoneText}>veya</p>
            <button 
              className={styles.uploadButton}
              onClick={(e) => {
                e.stopPropagation();
                inputRef.current.click();
              }}
              disabled={uploading}
              type="button"
            >
              {uploading ? (
                <>
                  <span className={styles.loadingSpinner}></span>
                  Yükleniyor...
                </>
              ) : (
                <>
                  <FaUpload />
                  Dosya Seçin
                </>
              )}
            </button>
          </div>

          <div className={styles.fileListSection}>
            <h3 className={styles.fileListTitle}>
              <FaFolder style={{ marginRight: '0.5rem' }} />
              Yüklenmiş Dosyalar
            </h3>
            
            {employee?.files && employee.files.length > 0 ? (
              <div className={styles.fileList}>
                {employee.files.map((file, index) => (
                  <div key={index} className={styles.fileItem}>
                    <span className={styles.fileCategory}>
                      {file.category || 'Kategorisiz'}
                    </span>
                    <div className={styles.fileInfo}>
                      <a 
                        href={file.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={styles.fileLink}
                      >
                        {getFileIcon(file.type || '')}
                        {file.name}
                      </a>
                      {file.uploadDate && (
                        <small style={{ color: 'var(--file-category)', fontSize: '0.7rem' }}>
                          {formatDate(file.uploadDate)}
                        </small>
                      )}
                    </div>
                    <button
                      className={styles.downloadButton}
                      onClick={() => downloadFileAs(file.url, file.name)}
                      title="Dosyayı İndir"
                    >
                      <FaDownload />
                    </button>
                    <button
                      className={styles.deleteButton}
                      onClick={() => handleDeleteFile(file)}
                      disabled={deleting}
                      title="Dosyayı Sil"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className={styles.noFilesMessage}>
                Bu çalışanın hiç dosyası bulunmamakta.
              </p>
            )}
          </div>

          {/* CV/GCP/IATA Belgeleri Bölümü */}
          <div className={styles.fileListSection} style={{ marginTop: '2rem' }}>
            <h3 className={styles.fileListTitle}>
              <FaFolder style={{ marginRight: '0.5rem' }} />
              CV/GCP/IATA Belgeleri
            </h3>
            
            {cvLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                <span className={styles.loadingSpinner}></span>
                <p style={{ marginTop: '1rem' }}>Yükleniyor...</p>
              </div>
            ) : cvDocuments.length > 0 ? (
              <div className={styles.cvDocumentList}>
                {[
                  { key: 'CV-TR', label: 'Türkçe CV' },
                  { key: 'CV-EN', label: 'İngilizce CV' },
                  { key: 'GCP', label: 'GCP' },
                  { key: 'IATA', label: 'IATA' }
                ].map(docType => {
                  const typeDocs = cvDocuments.filter(doc => doc.documentType === docType.key);
                  if (typeDocs.length === 0) return null;
                  
                  return (
                    <div key={docType.key} className={styles.cvDocumentGroup}>
                      <h4 className={styles.cvDocumentGroupTitle}>{docType.label}</h4>
                      {typeDocs.map((doc) => (
                        <div key={doc.id} className={styles.cvDocumentItem}>
                          <div className={styles.cvDocumentIcon}>
                            {doc.fileType === 'pdf' ? <FaFilePdf style={{ color: '#dc3545' }} /> : <FaFileWord style={{ color: '#0d6efd' }} />}
                          </div>
                          <div className={styles.cvDocumentInfo}>
                            <div className={styles.cvDocumentName}>{doc.fileName}</div>
                            <div className={styles.cvDocumentMeta}>
                              <span>{formatDate(doc.uploadedAt)}</span>
                              <span>{(doc.fileSize / 1024).toFixed(2)} KB</span>
                            </div>
                          </div>
                          <div className={styles.cvDocumentActions}>
                            <a
                              href={doc.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.cvActionButton}
                              title="Görüntüle"
                            >
                              <FaEye />
                            </a>
                            <button
                              type="button"
                              onClick={() => downloadFileAs(doc.fileUrl, doc.fileName)}
                              className={styles.cvActionButton}
                              title="İndir"
                            >
                              <FaDownload />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className={styles.noFilesMessage}>
                Bu çalışanın CV/GCP/IATA belgesi bulunmamakta.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUploadModal;